import type { DepositLookupResponse } from '@massilia/queries'

import { useInitiateDepositMutation } from '@massilia/queries'
import { Button, Input, Label } from '@massilia/ui'
import { ArrowLeft, CheckCircle, CreditCard, Loader2, XCircle } from 'lucide-react'
import { useMemo, useState } from 'react'

import { SummaryRow } from '@/components/payment-confirmation/summary-row'
import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { centsToEuros, formatPrice } from '@/utils/format-price'

interface DepositBookingInfoProps {
  booking: DepositLookupResponse
  credentials: { dossierNumber: string; email: string; lastName: string }
  onBack: () => void
}

export function DepositBookingInfo({ booking, credentials, onBack }: DepositBookingInfoProps) {
  const { isPending, mutateAsync: initiateDeposit } = useInitiateDepositMutation()
  const [amountEuros, setAmountEuros] = useState('')
  const [submitError, setSubmitError] = useState<null | string>(null)

  const paidAmount = useMemo(
    () => booking.payments.reduce((sum, p) => sum + p.amount, 0),
    [booking.payments]
  )

  const remainingAmount = booking.totalPrice - paidAmount
  const isFullyPaid = remainingAmount <= 0
  const isCancelled = booking.status === 'CANCELLED'
  const canPay = !isFullyPaid && !isCancelled

  const maxAmountEuros = centsToEuros(remainingAmount)

  const handleSubmit = async () => {
    setSubmitError(null)

    const parsedAmount = parseFloat(amountEuros.replace(',', '.'))
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setSubmitError('Veuillez saisir un montant valide.')
      return
    }

    const amountCents = Math.round(parsedAmount * 100)
    if (amountCents > remainingAmount) {
      setSubmitError(
        `Le montant ne peut pas dépasser le solde restant (${formatPrice(maxAmountEuros)}).`
      )
      return
    }

    try {
      const result = await initiateDeposit({
        ...credentials,
        amount: amountCents,
      })
      window.location.assign(result.axeptaRedirectUrl)
    } catch (error) {
      setSubmitError(resolveAuthErrorMessage(error))
    }
  }

  const formatDate = (isoDate: string) => {
    try {
      return new Intl.DateTimeFormat('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date(isoDate))
    } catch {
      return isoDate
    }
  }

  return (
    <div className="mx-auto w-full max-w-lg">
      <button
        className="text-brand-700 mb-4 flex items-center gap-1 text-sm font-medium hover:underline"
        onClick={onBack}
        type="button"
      >
        <ArrowLeft className="size-4" />
        Rechercher un autre dossier
      </button>

      <div className="border-gray-light-200 flex flex-col gap-5 rounded-2xl border bg-white p-6">
        {/* Booking info header */}
        <div>
          <h2 className="text-gray-light-900 text-xl font-semibold">Dossier {booking.reference}</h2>
          <p className="text-gray-light-600 mt-1 text-sm">
            {booking.departureLocation} → {booking.arrivalLocation}
            {' — '}
            {formatDate(booking.departureDate)}
            {booking.returnDate && ` au ${formatDate(booking.returnDate)}`}
          </p>
        </div>

        {/* Payment summary */}
        <div className="border-gray-light-200 flex flex-col gap-2 border-t pt-4">
          <SummaryRow label="Prix total" value={formatPrice(centsToEuros(booking.totalPrice))} />
          <SummaryRow label="Acomptes versés" value={formatPrice(centsToEuros(paidAmount))} />
          <div className="flex items-start justify-between gap-3 text-sm">
            <div className="text-gray-light-700 font-medium">Reste à payer</div>
            <div className="text-lg font-bold text-emerald-700">
              {formatPrice(centsToEuros(remainingAmount))}
            </div>
          </div>
        </div>

        {/* Payment history */}
        {booking.payments.length > 0 && (
          <div className="border-gray-light-200 flex flex-col gap-2 border-t pt-4">
            <h3 className="text-gray-light-900 text-sm font-semibold">Historique des paiements</h3>
            <div className="flex flex-col gap-1">
              {booking.payments.map((payment, index) => (
                <div
                  className="text-gray-light-700 flex items-center justify-between text-sm"
                  key={index}
                >
                  <span>{payment.paidAt ? formatDate(payment.paidAt) : 'Date inconnue'}</span>
                  <span className="font-medium">{formatPrice(centsToEuros(payment.amount))}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status messages */}
        {isFullyPaid && (
          <div className="flex items-center gap-3 rounded-lg bg-emerald-50 p-4">
            <CheckCircle className="size-5 shrink-0 text-emerald-600" />
            <p className="text-sm font-medium text-emerald-800">
              Votre dossier est entièrement réglé. Aucun paiement supplémentaire n&apos;est
              nécessaire.
            </p>
          </div>
        )}

        {isCancelled && (
          <div className="flex items-center gap-3 rounded-lg bg-red-50 p-4">
            <XCircle className="size-5 shrink-0 text-red-600" />
            <p className="text-sm font-medium text-red-800">
              Ce dossier a été annulé. Il n&apos;est plus possible d&apos;effectuer de paiement.
            </p>
          </div>
        )}

        {/* Payment form */}
        {canPay && (
          <div className="border-gray-light-200 flex flex-col gap-4 border-t pt-4">
            <h3 className="text-gray-light-900 text-sm font-semibold">Verser un acompte</h3>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="deposit-amount">Montant (en euros)</Label>
              <div className="relative">
                <Input
                  id="deposit-amount"
                  inputMode="decimal"
                  max={maxAmountEuros}
                  min={0.01}
                  onChange={(e) => {
                    setAmountEuros(e.target.value)
                    if (submitError) setSubmitError(null)
                  }}
                  placeholder={`Max. ${formatPrice(maxAmountEuros)}`}
                  step={0.01}
                  type="number"
                  value={amountEuros}
                />
                <span className="text-gray-light-400 pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm">
                  EUR
                </span>
              </div>
            </div>

            {submitError && <p className="text-sm font-medium text-red-600">{submitError}</p>}

            <Button
              className="bg-brand-600 hover:bg-brand-700 w-full px-4 py-3 text-base font-medium text-white"
              disabled={isPending || !amountEuros}
              onClick={() => void handleSubmit()}
              type="button"
            >
              {isPending ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <>
                  <CreditCard className="mr-2 size-4" />
                  Payer par carte bancaire
                </>
              )}
            </Button>

            <p className="text-gray-light-500 text-center text-xs">
              Vous serez redirigé vers notre plateforme de paiement sécurisée.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
